<template>
  <div class="height-100-p">
    <el-table
        :data="palletReceipt.list"
        v-loading="palletReceipt.loadingSearch"
        id="palletReceipt-data-list"
        size="mini"
        tooltip-effect="dark"
        sortable
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        @sort-change="handleSortChange"
        :default-sort="{prop: 'dayOfService', order: 'descending'}"
        stripe>
      <el-table-column
          prop="number"
          :label="$t('palletReceipt.receiptNumber')"
          :min-width="4"
          sortable="custom">
        <template slot-scope="scope">
          <span>{{ scope.row.number }}</span>
        </template>
      </el-table-column>
      <el-table-column
          prop="dayOfService"
          :label="$t('palletReceipt.dayOfService')"
          :min-width="5"
          sortable="custom">
        <template slot-scope="scope">
          <span>{{ scope.row.dayOfService | moment(dateDisplayFormat) }}</span>
        </template>
      </el-table-column>
      <el-table-column
          prop="shipper"
          :label="$t('palletReceipt.shipper')"
          :min-width="7"
          align="left"
          header-align="center"
          sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="`${ scope.row.shipper }`" placement="top">
            <span>{{ scope.row.shipper }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
          prop="expiryDate"
          :label="$t('palletReceipt.expiryDate')"
          :min-width="5"
          sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="`${ scope.row.shipper }`" placement="top">
            <span>{{ scope.row.expiryDate | moment(dateDisplayFormat) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
          prop="numberOfValid"
          :label="$t('palletReceipt.numberOfValid')"
          :min-width="3"
          align="center"
          sortable="custom">
        <template slot-scope="scope">
          <span>{{ scope.row.numberOfValid }}</span>
        </template>
      </el-table-column>
      <el-table-column
          prop="numberOfInvalid"
          :label="$t('palletReceipt.numberOfInvalid')"
          :min-width="3"
          align="center"
          sortable="custom">
        <template slot-scope="scope">
          <span>{{ scope.row.numberOfInvalid }}</span>
        </template>
      </el-table-column>
      <el-table-column
          prop="numberOfRedeemable"
          :label="$t('palletReceipt.numberOfRedeemable')"
          :min-width="4"
          align="center"
          sortable="custom">
        <template slot-scope="scope">
          <span>{{ scope.row.numberOfRedeemable }}</span>
        </template>
      </el-table-column>
      <el-table-column
          prop="supervisor"
          :label="$t('palletReceipt.supervisor')"
          :min-width="6"
          sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="`${ scope.row.supervisor }`" placement="top">
            <span>{{ scope.row.supervisor }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
          prop="status"
          :label="$t('palletReceipt.status')"
          :min-width="5"
          align="center"
          sortable="custom">
        <template slot-scope="scope">
          <span>{{ palletReceiptStatusLabel(scope.row.status) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="options" :label="$t('common.options')" :min-width="4" align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="$t('palletReceipt.print')" placement="top-start">
            <i class="el-icon-printer icon-action" @click="handlePrint(scope.row.id)"></i>
          </el-tooltip>
          <el-tooltip
              v-if="$can(permissions.RECEIVING.DELETE_PALLET_RECEIPTS)"
              class="item"
              effect="dark"
              :enterable="false"
              :content="$t('common.delete')"
              placement="top-start">
            <i class="el-icon-delete icon-action" @click="handleDelete(scope.row.id)"></i>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-footer" v-show="palletReceipt.total > 0">
      <span class="el-pagination__total" slot>
        <span class="el-pagination__title" slot>{{ $t('palletReceipt.totalPallets') }}</span>
           {{ palletReceipt.total }}
      </span>
      <el-pagination
          @size-change="handleSearchChangeLimit"
          :page-sizes="sizes"
          :page-size="palletReceipt.actualFilters.rows"
          :current-page.sync="palletReceipt.actualFilters.page"
          @current-change="handleSearchChangePage"
          :total="palletReceipt.total"
          layout="slot, sizes, prev, pager, next, jumper">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import { Loading } from 'element-ui';
import { mapGetters, mapActions } from 'vuex';
import constants from '@/utils/constants';
import transformLabels from '@/plugins/unitDataTransformLabels';
import { PALLET_RECEIPT_SHIPPER_SEARCH, PALLET_RECEIPT_DELETE, PALLET_RECEIPT_PRINT } from '@/store/actions';

export default {
  name: 'SearchSummary',
  mixins: [transformLabels],
  computed: {
    ...mapGetters(['palletReceipt']),
    dateDisplayFormat() {
      return constants.DATES.DEFAULT_DISPLAY_FORMAT;
    }
  },
  data() {
    return {
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES
    };
  },
  methods: {
    ...mapActions({
      search: PALLET_RECEIPT_SHIPPER_SEARCH,
      delete: PALLET_RECEIPT_DELETE,
      print: PALLET_RECEIPT_PRINT
    }),
    handleSearchChangePage(val) {
      this.search({ page: val });
    },
    handleSearchChangeLimit(val) {
      this.search({ rows: val, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSortChange({ prop, order }) {
      this.search({
        orderField: prop,
        orderDirection: constants.TABLES.ORDER_DIRECTION[order]
      });
    },
    async handleDelete(palletReceiptId) {
      const confirm = await this.$confirm(
        this.$t('palletReceipt.confirmDelete'),
        { confirmButtonText: this.$t('common.yes'), dangerouslyUseHTMLString: true }
      ).catch(() => {});
      if (confirm) {
        try {
          const response = await this.delete(palletReceiptId);
          this.$message.success(response.message);
        } finally {
          await this.$store.dispatch('palletReceipt/shipperSearch', {});
        }
      }
    },
    async handlePrint(palletReceiptId) {
      const loading = Loading.service(constants.LOADING.PRINT_PALLET_RECEIPT_CONFIG);
      await this.print(palletReceiptId);
      loading.close();
    }
  }
};
</script>
